import GridSpinner from 'components/UI/GridSpinner'
import ShowMoreButton from 'components/ShowMoreButton'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'modules/Recommendations/Benchmarks/benchmarks.module.scss'
import GroupActions from './subcomponents/GroupActions'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Recommendations/Benchmarks/filters'
import {
  DROP_LIST,
  FETCH,
  SET_FILTER,
  TOGGLE_ARCHIVE,
  UPDATE,
} from 'modules/Recommendations/Benchmarks/store/types'
import PageHeader from 'components/PageHeader'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import EmptyResults from 'components/EmptyResults'
import omit from 'lodash/omit'
import List from 'modules/Recommendations/Benchmarks/subcomponents/List'
import { Icon } from 'gu-ui-kit'

const BenchmarksList = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [checkedIDs, setCheckedIDs] = useState([])

  const { data, pagination, loading, archive, loaded, filters } = useSelector(
    (state) => state['recommendations/benchmarks'],
  )

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_FILTER,
    localStorageKey: 'recommendations/benchmarks',
  })

  useEffect(() => {
    return () => {
      dispatch({ type: UPDATE, payload: { loading: true, loaded: false } })
    }
  }, [])

  useEffect(() => {
    dispatch({ type: DROP_LIST })
    setPage(1)
    setCheckedIDs([])
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.params) return

    setCheckedIDs([])

    dispatch({
      type: FETCH,
      payload: {
        params: {
          ...filterManager.params,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
        },
      },
    })
  }, [filterManager.params, page])

  const handleChangePage = useCallback(() => {
    setPage((page) => page + 1)
  }, [setPage])

  const checkboxHandleChange = useCallback(
    (e, id = null) => {
      if (!id) {
        setCheckedIDs(e.target.checked ? data.map(({ id }) => id) : [])
      } else {
        if (checkedIDs.includes(id)) {
          setCheckedIDs(checkedIDs.filter((checkedID) => checkedID !== id))
        } else {
          const newChecked = checkedIDs.concat(id)
          setCheckedIDs(newChecked)
        }
      }
    },
    [checkedIDs, setCheckedIDs, data],
  )

  const action = useMemo(
    () => (filterManager.filters?.status === 'active' ? 'archive' : 'activate'),
    [filterManager.filters],
  )

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['status']),
    data,
    loaded,
    loading,
  )

  return (
    <div className={'layout-tile'}>
      {firstLoad ? (
        <GridSpinner width={32} />
      ) : totallyEmptyList ? (
        <EmptyResults
          className={styles['benchmarks-list_empty']}
          icon={<Icon name={'chart-search'} />}
          header={t('benchmarks::totally_empty_results.header')}
          text={t('benchmarks::totally_empty_results.text')}
        />
      ) : (
        <>
          <PageHeader
            className={'margin-bottom-24'}
            color={'Label-Primary'}
          >
            {t('benchmarks::header')}
          </PageHeader>

          <FilterPanel
            className={'margin-bottom-32'}
            filterManager={filterManager}
          />

          <GroupActions
            buttonLabel={t(`benchmarks::group_actions.${action}`)}
            data={data}
            checkboxHandleChange={checkboxHandleChange}
            checkedIDsLength={checkedIDs.length}
            archiveHandleClick={() =>
              new Promise((resolve, reject) => {
                dispatch({
                  meta: { resolve, reject },
                  type: TOGGLE_ARCHIVE,
                  payload: {
                    ids: checkedIDs,
                    action,
                  },
                })
              }).then(() => setCheckedIDs([]))
            }
          />

          {filterManager.filters && (
            <List
              processingIds={archive.processingIds}
              data={data}
              loading={loading}
              filters={filterManager.filters}
              checkboxHandleChange={checkboxHandleChange}
              checkedIDs={checkedIDs}
            />
          )}
          <ShowMoreButton
            onClick={handleChangePage}
            loading={loading}
            current={data.length}
            total={pagination.total}
          />
        </>
      )}
    </div>
  )
}

BenchmarksList.propTypes = {}

export default BenchmarksList
